// ajay:code all file this 24-07-26
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { productAPI, weightAPI } from "../../services/api";

const MIN_READINGS = 50;
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function TemplateView() {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [date, setDate] = useState(todayStr());
  const [fromTime, setFromTime] = useState("");
  const [toTime, setToTime] = useState("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null); // full API response: { product, meta, sampleSize, count, spc, ... }

  // useEffect(() => {
  //   productAPI
  //     .list()
  //     .then((r) => setProducts(r.data || []))
  //     .catch(() => {});
  // }, []);

  useEffect(() => {
    productAPI
      .list()
      .then((r) => {
        const activeOnly = (r.data || []).filter((p) => p.status === "Active");

        setProducts(activeOnly);
      })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { date };
      if (productId) params.product_id = productId;
      if (fromTime) params.from_time = fromTime;
      if (toTime) params.to_time = toTime;

      const res = await weightAPI.spcData(params);
      const d = res.data;

      if (!d.product) {
        setData(null);
        setError(d.message || "No data available");
        return;
      }
      if (!productId) setProductId(String(d.product.id));
      setData(d);
    } catch (e) {
      setError(e.message || "Failed to load data");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [productId, date, fromTime, toTime]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto updateded
  useEffect(() => {
    const isLiveMode = date === todayStr() && !fromTime && !toTime;
    if (!isLiveMode) return;
    const interval = setInterval(() => {
      load();
    }, 8000);
    return () => clearInterval(interval);
  }, [date, fromTime, toTime, load]);

  const spc = data?.spc;
  const product = data?.product;
  const meta = data?.meta;

  return (
    <div style={{ padding: 28 }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 20,
        }}
      >
        <button onClick={() => navigate("/company/templates")} style={iconBtn}>
          <ArrowLeft size={16} />
        </button>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700 }}>Weight SPC (Cp/Cpk)</h1>
          <p style={{ color: "var(--text2)", fontSize: 13, marginTop: 2 }}>
            Subgroup size 5 · up to 25 subgroups (125 readings), latest window
          </p>
        </div>
      </div>

      {/* Filters */}
      <div
        className="card"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 14,
          alignItems: "flex-end",
          marginBottom: 20,
        }}
      >
        <div className="form-group" style={{ minWidth: 200 }}>
          <label>Product</label>
          <select
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
          >
            <option value="">— Today's first scanned —</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.code})
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            max={todayStr()}
          />
        </div>
        <div className="form-group">
          <label>From Time</label>
          <input
            type="time"
            value={fromTime}
            onChange={(e) => setFromTime(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>To Time</label>
          <input
            type="time"
            value={toTime}
            onChange={(e) => setToTime(e.target.value)}
          />
        </div>
        <button
          className="btn-secondary"
          onClick={() => {
            setDate(todayStr());
            setFromTime("");
            setToTime("");
          }}
        >
          Reset to Today
        </button>
        <button
          className="btn-primary"
          onClick={load}
          style={{ display: "flex", alignItems: "center", gap: 6 }}
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {loading && <div style={{ color: "var(--text2)" }}>Loading...</div>}
      {!loading && error && (
        <div className="card" style={{ color: "var(--danger)" }}>
          {error}
        </div>
      )}

      {!loading && product && spc && (
        <>
          {/* ── Excel-style header block (Image 1) ── */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <h2
                style={{
                  fontSize: 16,
                  fontWeight: 800,
                  textAlign: "center",
                  flex: 1,
                }}
              >
                STATISTICAL PROCESS CONTROL STUDY
              </h2>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--text2)",
                  textAlign: "right",
                  lineHeight: 1.6,
                }}
              >
                <div>FORMAT No.: {meta.formatNo}</div>
                <div>CL.No.: {meta.clNo || "—"}</div>
                <div>Page No.: {meta.pageNo}</div>
              </div>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: 0,
                marginTop: 16,
                border: "1px solid var(--border)",
                borderRadius: 8,
                overflow: "hidden",
              }}
            >
              <HeaderCell label="PART NAME" value={product.name} />
              <HeaderCell label="INSTRUMENT" value={meta.instrument} />
              <HeaderCell label="L.COUNT" value={meta.leastCount} />
              <HeaderCell label="IMP." value={meta.impNote} span rowSpan />

              <HeaderCell label="PART NO." value={product.code} />
              <HeaderCell label="SPECIFICATION" value={product.specification} />
              <HeaderCell label="MACHINE" value={meta.machine} />

              {/*  <HeaderCell label="SAMPLE SIZE" value={data.sampleSize} />  */}
              <HeaderCell
                label="SAMPLE SIZE"
                value={`${spc.count} / ${data.sampleSize}`}
              />
              <HeaderCell label="OPERATION" value={meta.operation} />
              <HeaderCell label="DOC.NO./DATE" value={date} />
            </div>
          </div>

          {spc.insufficientData ? (
            <div
              className="card"
              style={{
                textAlign: "center",
                padding: 40,
                color: "var(--text2)",
              }}
            >
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>
                Not enough data yet
              </div>
              <div style={{ fontSize: 13 }}>
                {spc.count} of minimum {MIN_READINGS} readings collected —{" "}
                {spc.needed} more needed before Cp/Cpk can be calculated.
              </div>
            </div>
          ) : (
            <>
              {/* ── Verdict + big Cp/Cpk (Image 4 top) ── */}
              <div className="card" style={{ marginBottom: 20 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 20,
                  }}
                >
                  <VerdictRow
                    label="PROCESS CAPABILITY IS"
                    verdict={spc.cpVerdict}
                    title="Cp"
                    value={spc.cp}
                  />
                  <VerdictRow
                    label="PROCESS CAPABILITY INDEX IS"
                    verdict={spc.cpkVerdict}
                    title="Cpk"
                    value={spc.cpk}
                  />
                </div>
              </div>

              {/* ── R-Chart table + Histogram table (Image 2 top, no graphs) ── */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 16,
                  marginBottom: 20,
                }}
              >
                <div className="card" style={{ overflowX: "auto" }}>
                  <h3 style={sectionTitle}>R - Chart</h3>
                  <SimpleTable
                    headers={["#", "UCL", "r", "LCL"]}
                    rows={spc.rChartRows.map((row) => [
                      row.subgroup,
                      spc.controlLimits.rUCL,
                      row.r,
                      row.lcl,
                    ])}
                  />
                  <div
                    style={{
                      display: "flex",
                      gap: 24,
                      marginTop: 10,
                      fontSize: 12,
                    }}
                  >
                    <div>
                      Graphical R̄ UCL: <b>{spc.controlLimits.rUCL}</b>
                    </div>
                    <div>
                      Graphical R̄ LCL: <b>{spc.controlLimits.rLCL}</b>
                    </div>
                  </div>
                </div>
                <div className="card" style={{ overflowX: "auto" }}>
                  <h3 style={sectionTitle}>
                    Histogram &nbsp;
                    <span
                      style={{
                        fontWeight: 400,
                        fontSize: 11,
                        color: "var(--text3)",
                      }}
                    >
                      (USL {product.usl} · LSL {product.lsl} · bin width{" "}
                      {spc.histogram.binWidth})
                    </span>
                  </h3>
                  <SimpleTable
                    headers={["First", "Last", "Freq"]}
                    rows={spc.histogram.bins.map((b) => [
                      b.first,
                      b.last,
                      b.freq,
                    ])}
                  />
                </div>
              </div>

              {/* ── Calculation for Cp/Cpk (Image 2 bottom) ── */}
              <div className="card" style={{ marginBottom: 20 }}>
                <h3 style={sectionTitle}>Calculation for Cp/Cpk</h3>
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--text2)",
                    marginBottom: 10,
                  }}
                >
                  FOR N - SAMPLE SIZE = {spc.constantsUsed.N}
                </div>
                <SimpleTable
                  headers={["A2", "D3", "D4", "d2"]}
                  rows={[
                    [
                      spc.constantsUsed.A2,
                      spc.constantsUsed.D3,
                      spc.constantsUsed.D4,
                      spc.constantsUsed.d2,
                    ],
                  ]}
                />
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))",
                    gap: 10,
                    marginTop: 14,
                    fontSize: 12,
                    fontFamily: "var(--mono)",
                  }}
                >
                  {/* <FormulaRow label="X̄̄" value={spc.xBarBar} /> */}
                  <FormulaRow
                    label={<DoubleBar>X</DoubleBar>}
                    value={spc.xBarBar}
                  />
                  <FormulaRow label="R̄" value={spc.rBar} />
                  <FormulaRow
                    label="S = R̄/d2"
                    text={spc.formulas.sigma}
                    value={spc.sigma}
                  />
                  <FormulaRow label="A2.R̄" text={spc.formulas.a2rBar} />
                  <FormulaRow
                    label="UCLX"
                    text={spc.formulas.uclX}
                    value={spc.controlLimits.xBarUCL}
                  />
                  <FormulaRow
                    label="LCLX"
                    text={spc.formulas.lclX}
                    value={spc.controlLimits.xBarLCL}
                  />
                  <FormulaRow
                    label="UCLR"
                    text={spc.formulas.uclR}
                    value={spc.controlLimits.rUCL}
                  />
                  <FormulaRow
                    label="LCLR"
                    text={spc.formulas.lclR}
                    value={spc.controlLimits.rLCL}
                  />
                  <FormulaRow
                    label="Cp = T/6σ"
                    text={spc.formulas.cp}
                    value={spc.cp}
                    highlight
                  />
                  <FormulaRow
                    label={
                      <>
                        Cpk = (USL-<DoubleBar>X</DoubleBar>)/3S
                      </>
                    }
                    text={spc.formulas.cpu}
                  />
                  <FormulaRow
                    label={
                      <>
                        Cpk = (<DoubleBar>X</DoubleBar>-LSL)/3S
                      </>
                    }
                    text={spc.formulas.cpl}
                    highlight2={spc.cpk}
                  />
                </div>
                <div
                  style={{ marginTop: 12, fontSize: 11, color: "var(--text3)" }}
                >
                  T = TOTAL TOLERANCE (USL-LSL) = {spc.totalTolerance}{" "}
                  &nbsp;·&nbsp; For Cpk, minimum value of both side tolerance is
                  taken. For single-side tolerance, do not consider minimum
                  value.
                </div>
              </div>

              {/* ── X-Chart table (Image 3) ── */}
              <div
                className="card"
                style={{ marginBottom: 20, overflowX: "auto" }}
              >
                <h3 style={sectionTitle}>X - Chart</h3>
                <SimpleTable
                  headers={["#", "UCL", "X̄", "LCL"]}
                  rows={spc.xChartRows.map((row) => [
                    row.subgroup,
                    row.ucl,
                    row.xBar,
                    row.lcl,
                  ])}
                />
                <div
                  style={{
                    display: "flex",
                    gap: 24,
                    marginTop: 10,
                    fontSize: 12,
                  }}
                >
                  <div>
                    Graphical X̄ UCL: <b>{spc.controlLimits.xBarUCL}</b>
                  </div>
                  <div>
                    Graphical X̄ LCL: <b>{spc.controlLimits.xBarLCL}</b>
                  </div>
                </div>
              </div>

              {/* ── Raw data grid (SHIFT/DATE/X1-X5/SX/X/R) ── */}
              <div className="card" style={{ overflowX: "auto" }}>
                <h3 style={sectionTitle}>Raw Data</h3>
                <table
                  style={{
                    borderCollapse: "collapse",
                    fontSize: 12,
                    minWidth: 700,
                  }}
                >
                  <tbody>
                    <GridRow
                      label=""
                      cells={spc.subgroups.map((_, i) => i + 1)}
                      header
                    />
                    <GridRow
                      label="SHIFT"
                      cells={spc.subgroups.map(() => 1)}
                      highlight
                    />
                    <GridRow
                      label="DATE"
                      cells={spc.subgroups.map(() => date)}
                      highlight
                    />
                    {[0, 1, 2, 3, 4].map((rowIdx) => (
                      <GridRow
                        key={rowIdx}
                        label={`X${rowIdx + 1}`}
                        cells={spc.subgroups.map((sg) =>
                          sg.values[rowIdx] !== undefined
                            ? sg.values[rowIdx]
                            : "",
                        )}
                      />
                    ))}
                    <GridRow
                      label="SX"
                      cells={spc.subgroups.map((sg) =>
                        sg.mean !== null
                          ? round3(sg.mean * sg.values.length)
                          : "",
                      )}
                      highlight
                    />
                    <GridRow
                      label="X̄"
                      cells={spc.subgroups.map((sg) =>
                        sg.mean !== null ? sg.mean : "",
                      )}
                      highlight
                    />
                    <GridRow
                      label="R"
                      cells={spc.subgroups.map((sg) =>
                        sg.range !== null ? sg.range : "",
                      )}
                      highlight
                    />
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

const iconBtn = {
  background: "var(--surface2)",
  border: "1px solid var(--border)",
  borderRadius: 6,
  padding: 6,
  cursor: "pointer",
  color: "var(--text2)",
  display: "flex",
};
const sectionTitle = { fontSize: 13, fontWeight: 700, marginBottom: 10 };

function HeaderCell({ label, value, rowSpan }) {
  return (
    <div
      style={{
        padding: "8px 12px",
        borderRight: "1px solid var(--border)",
        borderBottom: "1px solid var(--border)",
        gridRow: rowSpan ? "span 3" : undefined,
        background: rowSpan ? "rgba(0,255,150,0.05)" : undefined,
      }}
    >
      <div style={{ fontSize: 10, color: "var(--text3)", marginBottom: 3 }}>
        {label}
      </div>
      <div style={{ fontSize: 12, fontWeight: 600 }}>{value ?? "—"}</div>
    </div>
  );
}

function VerdictRow({ label, verdict, title, value }) {
  const good = value !== null && value >= 1.33;
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 700 }}>
        {label}{" "}
        <span style={{ color: good ? "var(--success)" : "var(--danger)" }}>
          {verdict}
        </span>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 8,
          marginTop: 4,
        }}
      >
        <span style={{ fontSize: 12, color: "var(--text2)" }}>{title}</span>
        <span
          style={{
            fontSize: 26,
            fontWeight: 800,
            color: good ? "var(--success)" : "var(--danger)",
          }}
        >
          {value !== null ? value : "—"}
        </span>
      </div>
    </div>
  );
}

function SimpleTable({ headers, rows }) {
  return (
    <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
      <thead>
        <tr>
          {headers.map((h) => (
            <th
              key={h}
              style={{
                textAlign: "left",
                padding: "4px 8px",
                color: "var(--text3)",
                borderBottom: "1px solid var(--border)",
              }}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            {row.map((cell, j) => (
              <td
                key={j}
                style={{
                  padding: "4px 8px",
                  fontFamily: "var(--mono)",
                  borderBottom: "1px solid var(--border)",
                }}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function FormulaRow({ label, text, value, highlight, highlight2 }) {
  return (
    <div
      style={{
        background: "var(--surface2)",
        borderRadius: 8,
        padding: "8px 10px",
      }}
    >
      <div
        style={{
          fontSize: 10,
          color: "var(--text3)",
          marginBottom: 2,
          fontFamily: "inherit",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 12,
          color:
            highlight || highlight2 !== undefined
              ? "var(--accent)"
              : "var(--text)",
        }}
      >
        {text || value}
      </div>
    </div>
  );
}

function GridRow({ label, cells, header, highlight }) {
  return (
    <tr style={{ background: highlight ? "var(--surface2)" : "transparent" }}>
      <td
        style={{
          padding: "6px 10px",
          fontWeight: 700,
          fontSize: 11,
          color: "var(--text2)",
          borderRight: "1px solid var(--border)",
          borderBottom: "1px solid var(--border)",
          position: "sticky",
          left: 0,
          background: "var(--surface)",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </td>
      {cells.map((c, i) => (
        <td
          key={i}
          style={{
            padding: "6px 10px",
            textAlign: "center",
            fontFamily: "var(--mono)",
            fontWeight: header ? 700 : 400,
            color: header ? "var(--text3)" : "var(--text)",
            borderBottom: "1px solid var(--border)",
            minWidth: 56,
          }}
        >
          {c}
        </td>
      ))}
    </tr>
  );
}

function round3(v) {
  return Math.round(v * 1000) / 1000;
}

function SingleBar({ children }) {
  return (
    <span style={{ borderTop: "1px solid currentColor", padding: "0 2px" }}>
      {children}
    </span>
  );
}
function DoubleBar({ children }) {
  return (
    <span style={{ borderTop: "3px double currentColor", padding: "0 2px" }}>
      {children}
    </span>
  );
}